"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
export default function Standards() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const standards = [{
    numeral: "I.",
    title: "Trauma-Informed Care",
    description: "We recognize the impact of high-conflict separation on children. Our services are designed to minimize stress, prioritize emotional safety, and maintain a neutral, calm environment for every family member through trauma-informed engagement."
  }, {
    numeral: "II.",
    title: "Neutral & Objective Reporting",
    description: "Our documentation is strictly factual, child-centered, and court-ready. We adhere to rigorous neutrality requirements and performance standards expected by legal partners, providing unbiased observations that assist the court in making informed decisions."
  }, {
    numeral: "III.",
    title: "Structured Safety Protocols",
    description: "Safety is our foundation. From secure intake procedures to monitored exchanges and supervised parenting time, we maintain firm, consistent boundaries and accountability measures to ensure a protected space for all participants."
  }, {
    numeral: "IV.",
    title: "Professional & Qualified Staff",
    description: "Our team consists of credentialed professionals with verified qualifications in family support. We operate under strict professional ethics, clear scope of practice limitations, and robust conflict-of-interest protections, supported by qualified clinical supervision."
  }, {
    numeral: "V.",
    title: "Child-Centered Focus",
    description: "Every service is centered on the child's developmental needs. We maintain high performance standards to create an atmosphere where children feel secure, respected, and free from the pressures of adult conflict or litigation."
  }, {
    numeral: "VI.",
    title: "Clear & Consistent Communication",
    description: "We maintain transparent communication with all parties and court staff. Our intake process ensures everyone understands their roles, professional boundaries, and the accountability measures governing our neutral role."
  }, {
    numeral: "VII.",
    title: "Respectful & Compassionate Service",
    description: "We approach every family with dignity and compassion while holding firm professional boundaries. Our goal is to support families through difficult transitions with unwavering professionalism and ethical integrity."
  }, {
    numeral: "VIII.",
    title: "Arizona-Based Expertise",
    description: "Deeply rooted in the Arizona community, we understand the local legal landscape and the specific regulatory standards required for court-involved matters across the state, ensuring compliance and reliability."
  }];
  return <section data-section-id="3017" id="why-refer-to-us" className="dark bg-background text-foreground py-24 md:py-32 lg:py-[180px] px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div ref={introRef} className={`lg:sticky lg:top-32 transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-mono text-xs uppercase tracking-[0.3em] mb-10" style={{
              color: "var(--primary)"
            }}>
                — Why Refer to Us
              </p>

              <h2 className="font-default font-light text-4xl md:text-5xl lg:text-[56px] leading-[1.05] mb-10">
                Trust, safety,
                <br />
                <span className="italic" style={{
                color: "var(--primary)"
              }}>
                  and neutrality.
                </span>
                <br />
                Every time.
              </h2>

              <p className="font-default text-lg leading-relaxed max-w-md" style={{
              color: "var(--muted-foreground)"
            }}>
                Eight core principles guide our work at Tiny to Teen Supervised Parenting & Education Services.
                They are the foundation of our commitment to providing safe, 
                structured, and child-centered support for families navigating 
                complex transitions in Arizona.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {standards.map((item, i) => {
              return <StandardEntry key={i} data-index={i} numeral={item.numeral} title={item.title} description={item.description} isLast={i === standards.length - 1} index={i} />;
            })}

              <div className="mt-16 flex flex-col items-center text-center">
                <div className="font-default text-2xl mb-6" style={{
                color: "var(--primary)"
              }} aria-hidden="true">
                  ◆
                </div>
                <p className="font-default italic text-lg md:text-xl leading-relaxed max-w-md" style={{
                color: "var(--muted-foreground)"
              }}>
                  We are dedicated to providing the steady, professional support 
                  families need to move forward with confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function StandardEntry({
  numeral,
  title,
  description,
  isLast,
  index,
  ...rest
}: {
  numeral: string;
  title: string;
  description: string;
  isLast: boolean;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${Math.min(index * 60, 240)}ms`
  }}>
      <div className="pb-12">
        <p className="font-default italic text-base mb-4" style={{
        color: "var(--primary)"
      }}>
          {numeral}
        </p>

        <Separator className="mb-6 h-px" style={{
        backgroundColor: "var(--primary)",
        opacity: 0.4
      }} />

        <h3 className="font-default text-2xl md:text-[26px] leading-snug mb-4 font-normal">
          {title}
        </h3>

        <p className="font-default text-base leading-relaxed" style={{
        color: "var(--muted-foreground)"
      }}>
          {description}
        </p>
      </div>

      {!isLast && <Separator className="mb-12 h-px" style={{
      backgroundColor: "var(--border)"
    }} />}
    </div>;
}